import React, { FC, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import { CloseDialogFunc } from '../../hooks/useCloseDialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import { AccountSingleData } from '../AccountData/AccountSingleData';
import { LicenseContext } from '../../contexts/LicenseContext';
import { LazyUser } from '../LazyData/LazyUser';
import { Chip, styled } from '@mui/material';
import { Date } from '../Date/Date';
import { DateFormat } from '../../helpers/dateFormat';
import { useTranslation } from 'react-i18next';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { GuardedRouteLink } from '../../resources/GuardedRouteLink';
import Link from '@mui/material/Link';
import { createFetcher, useAutoFetch } from '../../contexts/Fetcher';
import { apiLicense } from '../../resources/api/apiLicense';
import { LazyOrganization } from '../LazyOrganization/LazyOrganization';
import { NoOrganizationContextProvider } from '../../contexts/OrganizationContextProvider';

interface IProps {
  isOpen: boolean;
  onClose: CloseDialogFunc;
}

const DataContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  gap: theme.spacing(2),
}));

const PathContainer = styled('div')(({ theme }) => ({
  overflow: 'auto',
  fontFamily: 'monospace',
  background: theme.palette.background.paper,
  padding: theme.spacing(1),
  borderRadius: theme.spacing(0.25),
}));

const { useGetLicenseDetails } = apiLicense;

const LicenseDetailsFetcher = createFetcher(() => {
  const license = useContext(LicenseContext);
  const { organization } = useContext(OrganizationContext);
  return useGetLicenseDetails(organization ?? void 0, license?.userUuid, license?.id).execute;
});

/**
 * @description Needs to be rendered inside LicenseContext and OrganizationContext
 */
export const LicenseDetailsModalComponent: FC<IProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const { organization } = useContext(OrganizationContext);
  const license = useContext(LicenseContext);
  const { data: detailsData } = useContext(LicenseDetailsFetcher.Context);

  useAutoFetch(LicenseDetailsFetcher, !!license);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>{t('licenseDetails.title')}</DialogTitle>
      <DialogContent>
        <DataContainer>
          <AccountSingleData label={t('licenseDetails.headers.id')}>{license?.id}</AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.name')}>
            {license?.name}
            {license?.isAddon ? <Chip color="info" label={t('assignLicenses.addonChip')} /> : null}
          </AccountSingleData>

          <AccountSingleData label={t('licenseDetails.headers.customerProductId')}>
            <Link
              component={GuardedRouteLink}
              route={'OrganizationProduct'}
              params={[organization?.name, license?.customerProductId]}
            >
              {license?.customerProductId}
            </Link>
          </AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.expiresAt')}>
            {license?.expiresAt ? <Date date={license?.expiresAt} format={DateFormat.DateFullWithTimeAndZone} /> : '-'}
          </AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.updatesExpiresAt')}>
            {license?.updatesExpiresAt ? (
              <Date date={license?.updatesExpiresAt} format={DateFormat.DateFullWithTimeAndZone} />
            ) : (
              '-'
            )}
          </AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.offlineExpiresAt')}>
            {license?.offlineExpiresAt ? (
              <Date date={license?.offlineExpiresAt} format={DateFormat.DateFullWithTimeAndZone} />
            ) : (
              '-'
            )}
          </AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.suspended')}>
            {license?.suspended ? <Chip color="warning" label={t('common.licenseStatus.suspended')} /> : null}
          </AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.mode')}>{license?.mode}</AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.planType')}>{license?.planType}</AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.organizationUuid')}>
            {license ? <LazyOrganization id={license.organizationUuid} /> : null}
          </AccountSingleData>

          <AccountSingleData label={t('licenseDetails.headers.planDuration')}>
            {license?.planDuration}
          </AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.packageId')}>{license?.packageId}</AccountSingleData>
          <AccountSingleData label={t('licenseDetails.headers.planId')}>{license?.planId}</AccountSingleData>

          <AccountSingleData label={t('licenseDetails.headers.user')}>
            <NoOrganizationContextProvider>
              {license?.userUuid ? <LazyUser id={license?.userUuid} /> : null}
            </NoOrganizationContextProvider>
          </AccountSingleData>

          <AccountSingleData label={t('licenseDetails.headers.offlineLicensePath')}>
            {license?.offlineLicensePath ? <PathContainer>{license?.offlineLicensePath}</PathContainer> : null}
          </AccountSingleData>

          {detailsData?.machine ? (
            <>
              <AccountSingleData label={t('licenseDetails.headers.machine.fileToken')}>
                <PathContainer>{detailsData.machine.fileToken}</PathContainer>
              </AccountSingleData>
              <AccountSingleData label={t('licenseDetails.headers.machine.fingerprint')}>
                <PathContainer>{detailsData.machine.fingerprint}</PathContainer>
              </AccountSingleData>
              <AccountSingleData label={t('licenseDetails.headers.machine.hostname')}>
                {detailsData.machine.hostname ?? detailsData.machine.name}
              </AccountSingleData>
              <AccountSingleData label={t('licenseDetails.headers.machine.id')}>
                {detailsData.machine.id}
              </AccountSingleData>
              <AccountSingleData label={t('licenseDetails.headers.machine.ip')}>
                {detailsData.machine.ip}
              </AccountSingleData>
              <AccountSingleData label={t('licenseDetails.headers.machine.platform')}>
                {detailsData.machine.platform}
              </AccountSingleData>
              <AccountSingleData label={t('licenseDetails.headers.machine.updated')}>
                <Date date={detailsData.machine.updated} format={DateFormat.DateFullWithTimeAndZone} />
              </AccountSingleData>
              <AccountSingleData label={t('licenseDetails.headers.machine.created')}>
                <Date date={detailsData.machine.created} format={DateFormat.DateFullWithTimeAndZone} />
              </AccountSingleData>
            </>
          ) : null}

          <AccountSingleData label={t('licenseDetails.headers.tools')}>
            {detailsData?.toolsLicenses.map((tool) => (
              <Chip color={'default'} label={tool.tool} key={tool.id} />
            ))}
          </AccountSingleData>
        </DataContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.dialog.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export const LicenseDetailsModal: FC<IProps> = (props) => {
  return (
    <LicenseDetailsFetcher.WMF>
      <LicenseDetailsModalComponent {...props} />
    </LicenseDetailsFetcher.WMF>
  );
};
